/**
 * @file Semantic color tokens for both color schemes.
 *
 * `semanticPalette` holds the real hex/rgba values per scheme — use it (via
 * `useThemeColors()`) in JS contexts that need literal colors (ApexCharts
 * configs, Google Maps options, color math).
 *
 * `semanticColors` keeps the original key paths but every leaf is a
 * `var(--dt-…)` reference resolved per scheme by cssVariablesResolver, so all
 * existing style/CSS consumers react to the color scheme without edits.
 */
import { coral, teal, sand, charcoal } from './colors';

const rgbaFromHex = (hex, alpha) =>
  `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}, ${alpha})`;

const light = {
  surface: {
    base: '#FAFAF8',
    raised: '#F4F2EC',
    sunken: sand[0],
    overlayRaised: '#F4F2EC',
    hover: 'rgba(30, 41, 59, 0.04)'
  },
  border: {
    default: '#E4E0D6',
    strong: charcoal[2]
  },
  text: {
    primary: charcoal[9],
    secondary: charcoal[6],
    muted: charcoal[4],
    inverse: '#FAFAF8'
  },
  brand: {
    coralText: coral[6],
    coralFill: coral[6],
    coralFillHover: coral[7],
    coralFillActive: coral[7],
    coralFillLabel: '#FAFAF8',
    coralSubtleBg: rgbaFromHex(coral[6], 0.08),
    tealText: teal[6],
    tealFill: teal[6],
    tealFillHover: teal[7],
    tealSubtleBg: rgbaFromHex(teal[6], 0.08),
    coralSurface: coral[0],
    coralSurfaceBorder: coral[2],
    coralSurfaceBorderHover: coral[4],
    sandSurface: sand[0],
    sandSurfaceBorder: sand[2],
    sandSurfaceBorderHover: sand[4],
    sandHighlightBg: sand[0],
    sandHighlightBorder: sand[2],
    sandAccentText: sand[7]
  },
  status: {
    success: teal[6],
    error: '#D14343',
    warning: '#E0A458',
    info: '#3B82A8',
    successBg: rgbaFromHex(teal[6], 0.1),
    errorBg: 'rgba(209, 67, 67, 0.1)',
    warningBg: 'rgba(224, 164, 88, 0.1)',
    infoBg: 'rgba(59, 130, 168, 0.1)'
  },
  focus: {
    ring: rgbaFromHex(coral[6], 0.4)
  },
  overlay: {
    scrim: 'rgba(30, 41, 59, 0.48)'
  },
  skeleton: {
    base: 'rgba(30, 41, 59, 0.06)',
    highlight: 'rgba(30, 41, 59, 0.1)'
  }
};

// Warm graphite (hue 30–38°) — the light theme's paper turned down, not cold slate.
const dark = {
  surface: {
    base: '#1A1714',
    raised: '#23201B',
    sunken: '#131110',
    overlayRaised: '#2A261F',
    hover: 'rgba(250, 245, 235, 0.06)'
  },
  border: {
    default: 'rgba(250, 245, 235, 0.1)',
    strong: 'rgba(250, 245, 235, 0.22)'
  },
  text: {
    primary: '#FAFAF8',
    secondary: '#C9C2B8',
    muted: '#948D82',
    inverse: '#141110'
  },
  brand: {
    coralText: coral[4],
    coralFill: coral[5],
    coralFillHover: coral[4],
    coralFillActive: coral[6],
    // Dark ink label — white on coral[5] fails contrast (2.9:1)
    coralFillLabel: '#141110',
    coralSubtleBg: rgbaFromHex(coral[4], 0.14),
    tealText: teal[4],
    tealFill: teal[5],
    tealFillHover: teal[4],
    tealSubtleBg: rgbaFromHex(teal[4], 0.12),
    // Coral-tinted graphite (coral[4] at ~10% over surface.base) for premium
    // card surfaces — solid so text contrast doesn't depend on the parent.
    coralSurface: '#2B1E20',
    coralSurfaceBorder: rgbaFromHex(coral[4], 0.3),
    coralSurfaceBorderHover: rgbaFromHex(coral[4], 0.55),
    // Sand-tinted graphite (sand[3] at ~10% over surface.base), same recipe.
    sandSurface: '#2F2A23',
    sandSurfaceBorder: rgbaFromHex(sand[3], 0.28),
    sandSurfaceBorderHover: rgbaFromHex(sand[3], 0.5),
    sandHighlightBg: rgbaFromHex(sand[3], 0.1),
    sandHighlightBorder: rgbaFromHex(sand[3], 0.25),
    sandAccentText: sand[3]
  },
  status: {
    success: teal[4],
    error: '#E36D6D',
    warning: '#E0A458',
    info: '#6FAECF',
    successBg: rgbaFromHex(teal[4], 0.12),
    errorBg: 'rgba(227, 109, 109, 0.12)',
    warningBg: 'rgba(224, 164, 88, 0.12)',
    infoBg: 'rgba(111, 174, 207, 0.12)'
  },
  focus: {
    ring: rgbaFromHex(coral[4], 0.55)
  },
  overlay: {
    scrim: 'rgba(12, 10, 8, 0.65)'
  },
  skeleton: {
    base: 'rgba(250, 245, 235, 0.07)',
    highlight: 'rgba(250, 245, 235, 0.12)'
  }
};

export const semanticPalette = { light, dark };

const toVars = (obj, path = []) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      typeof value === 'object' && value !== null ? toVars(value, [...path, key]) : `var(--dt-${[...path, key].join('-')})`
    ])
  );

export const semanticColors = toVars(light);
